import { invalidateQueries, queryKeys } from "@/shared/lib/queryClient";
import AuthService from "@/shared/services/AuthService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

// Основной хук для авторизации - единая точка истины
export const useAuth = () => {
  const {
    data: authData,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: AuthService.checkAuth,
    staleTime: 30 * 1000, // Сократил время актуальности до 30 секунд
    gcTime: 5 * 60 * 1000, // Кэш храним 5 минут
    retry: false, // Убираю все повторы для четкой логики
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Авторизован = есть данные пользователя и нет ошибки
  const isAuthenticated = !!authData && !isError;

  return {
    user: authData ?? null,
    isAuthenticated,
    isLoading,
    error,
  };
};

// Хук для логина
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AuthService.login,
    onSuccess: async () => {
      // После успешного логина токены сохранены в localStorage и проставлен Authorization
      // Получаем актуальные данные пользователя
      const userData = await AuthService.checkAuth();
      queryClient.setQueryData(queryKeys.auth.me(), userData);
      invalidateQueries.auth();
    },
    onError: (error: AxiosError) => {
      console.error("Login failed:", error);
    },
  });
};

// Хук для регистрации
export const useRegister = () => {
  return useMutation({
    mutationFn: AuthService.register,
    onSuccess: () => {
      // Не сохраняем user в кэш — доступ только после верификации email
      invalidateQueries.auth();
    },
    onError: (error: AxiosError) => {
      console.error("Registration failed:", error);
    },
  });
};

// Хук для логаута
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AuthService.logout,
    onSuccess: () => {
      // Очищаем все данные авторизации из кэша
      queryClient.removeQueries({ queryKey: queryKeys.auth.all });

      // Очищаем весь кэш для безопасности
      queryClient.clear();
    },
    onError: (error: AxiosError) => {
      // Даже при ошибке очищаем локальные данные
      queryClient.removeQueries({ queryKey: queryKeys.auth.all });
      queryClient.clear();

      console.error("Logout failed:", error);
    },
  });
};

// Хук для обновления токена
// Удален, так как метод refreshToken удален из AuthService (нет в новой документации)
// export const useRefreshToken = () => { ... }

// Хук для верификации email
// Удален, так как метод verifyEmail удален из AuthService (заменен на process_otp)
// Для обработки OTP используйте useProfile hooks с processOtp/processEmailOtp
// export const useVerify = () => { ... }

// Простой хук для быстрой проверки авторизации без загрузки данных пользователя
export const useIsAuthenticated = () => {
  const { isAuthenticated, isLoading } = useAuth();
  return { isAuthenticated, isLoading };
};
